/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Form as NXUIForm, Select, Table } from '@nexigendigital/nxui'
import { useDomainAvailabilityQuery, useProductQuery, useUpdateCartItemsMutation } from 'api/shop'
import { Availability, Cart, PendingItem, Pricing } from 'models/shop'
import { OrderType } from 'models/enums'
import {
    addToItems,
    removeFromItemsByNameID,
    toggleAutoRenew,
    toggleIDProtection,
    updateBillingCycle
} from 'store/slices/shopSlice'
import { aud, getBillingCycle, getDomainName } from 'helpers/utils'
import { useAppDispatch, useAppSelector } from 'store/hooks'

import Search from 'containers/shop/domains/search'
import EPPLightbox from 'containers/shop/domains/lightbox/EPPLightbox'
import ContactDetailsLightbox from 'containers/shop/domains/lightbox/contactDetailsLightbox'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface PricingProps {
    domain: {
        pricing: Pricing[]
    },
    searchTerm: string | undefined,
    billingCycle: number | null,
    setBillingCycle: (value: number) => void,
    addedItem: object | undefined
}

interface AutoRenewProps {
    id: number,
    domain: {
        premium: boolean
    },
    searchTerm: string | undefined,
    addedItem: PendingItem | undefined
}

interface IdProtectionProps {
    id: number,
    allowIdProtection: boolean,
    searchTerm: string | undefined,
    addedItem: PendingItem | undefined
}

interface SelectItemProps {
    domain: Availability,
    items: PendingItem[],
    searchTerm: string | undefined,
    billingCycle: number | null,
    setLightboxStatus: (state: boolean) => void,
    cart: Cart | null
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Transfer() {
    const dispatch = useAppDispatch()
    const [billingCycle, setBillingCycle] = useState<number | null>(0)
    const [lightboxStatus, setLightboxStatus] = useState(false)
    const [openLightbox, setOpenLightbox] = useState(false)
    const { searchTerm, items, cart, product } = useAppSelector((state) => state.shop)
    const { isSuccess, data: { domain, name } = {} } = useDomainAvailabilityQuery(searchTerm, { skip: !searchTerm })
    const { data, isSuccess: isProductSuccess } = useProductQuery(product, { skip: !product })
    const transferredItem = items.find((item) => item.name === name)
    const hasDomainContact = items.filter(({ product_type }) => {
        return product_type === 'domain'
    }).every(({ domain_contact_fields }: PendingItem) => {
        return domain_contact_fields
    })
    const columns = useMemo(() => ([
        {
            Header: 'Domain',
            accessor: 'domain'
        },
        {
            Header: 'Price',
            accessor: 'price'
        },
        {
            Header: 'Auto-Renew',
            accessor: 'autoRenew'
        },
        {
            Header: 'ID Protection',
            accessor: 'idProtection'
        },
        {
            Header: 'Transfer Requirement',
            accessor: 'transferRequirement'
        },
        {
            Header: '',
            accessor: 'action',
            align: 'right'
        }
    ]), [])

    useEffect(() => {
        if (isSuccess && domain) {
            const { billing_cycle_id } = domain.pricing.filter(({ type }: Pricing) => type === 'transfer')[0]

            setBillingCycle(billing_cycle_id)
        }
    }, [domain, isSuccess])

    useEffect(() => {
        if (isProductSuccess && data?.service_type === 'domain' && !hasDomainContact) {
            setOpenLightbox(true)
        }
    }, [hasDomainContact, isProductSuccess, data])

    useEffect(() => {
        if (!openLightbox && isProductSuccess && data?.service_type === 'domain') {
            const timer = setTimeout(() => {
                const itemName = `${getDomainName(searchTerm || '')}${data.name}`
                const domain = items.find(({ name, product_type }: PendingItem) => {
                    return product_type === 'domain' && name === itemName
                })

                if (domain && !domain?.domain_contact_fields) {
                    dispatch(removeFromItemsByNameID({ product_id: data.id, name: itemName }))
                }
            }, 500)

            return () => clearTimeout(timer)
        }
    }, [openLightbox, isProductSuccess])

    function Result() {
        const rows = useMemo(() => {
            if (isSuccess && domain) {
                const { product: { id }, meta: { allow_id_protection, require_epp_code } } = domain
                const addedItem = items.find((item: PendingItem) => item.name === searchTerm)

                return [{
                    domain: <DomainName>{searchTerm}</DomainName>,
                    price: (
                        <TransferPricing
                            domain={domain}
                            searchTerm={searchTerm}
                            billingCycle={billingCycle}
                            setBillingCycle={setBillingCycle}
                            addedItem={addedItem}
                        />
                    ),
                    autoRenew: (
                        <AutoRenew
                            id={id}
                            domain={domain}
                            searchTerm={searchTerm}
                            addedItem={addedItem}
                        />
                    ),
                    idProtection: (
                        <IdProtection
                            id={id}
                            allowIdProtection={allow_id_protection}
                            searchTerm={searchTerm}
                            addedItem={addedItem}
                        />
                    ),
                    transferRequirement: require_epp_code ? <RowWrapper>EPP Code</RowWrapper> : null,
                    action: (
                        <SelectItem
                            domain={domain}
                            items={items}
                            searchTerm={searchTerm}
                            billingCycle={billingCycle}
                            setLightboxStatus={setLightboxStatus}
                            cart={cart}
                        />
                    )
                }]
            }

            return []
        }, [isSuccess, domain])

        return (
            <Table
                render={{
                    columns: () => columns,
                    data: () => rows
                }}
                conditions={{
                    sort: false
                }}
            />
        )
    }

    /*   RENDER COMPONENT
    **********************************************************************************************************/
    return (
        <StyledTransfer>
            <Search type={OrderType.Transfer}/>
            <Result/>
            {(!!transferredItem && hasDomainContact && isSuccess && domain) &&
                <EPPLightbox
                    domain={domain}
                    name={name}
                    status={lightboxStatus}
                    close={() => setLightboxStatus(false)}
                />
            }
            {data &&
                <ContactDetailsLightbox domain={data} status={openLightbox} close={() => setOpenLightbox(false)}/>
            }
        </StyledTransfer>
    )
}

const TransferPricing = ({ domain, searchTerm, billingCycle, setBillingCycle, addedItem }: PricingProps) => {
    const dispatch = useDispatch()
    const { pricing } = domain

    return (
        <RowWrapper>
            <Select value={billingCycle} disabled={!addedItem} onChange={(e: ChangeEvent<{ value: string }>) => {
                const value = +e.target.value

                setBillingCycle(value)

                dispatch(updateBillingCycle({
                    name: searchTerm,
                    billing_cycle_id: value,
                    type: 'transfer'
                }))
            }}>
                {pricing.filter(({ type }: { type: string }) => type === 'transfer').map((pricingList) => (
                    <option key={pricingList.billing_cycle_id} value={pricingList.billing_cycle_id}>
                        {aud(pricingList.price)}
                        {' '}
                        {pricingList.billing_cycle
                            ? getBillingCycle(pricingList.billing_cycle.name)
                            : `/${pricingList.billing_cycle_id}`
                        }
                    </option>
                ))}
            </Select>
        </RowWrapper>
    )
}

const AutoRenew = ({ id, domain, searchTerm, addedItem }: AutoRenewProps) => {
    const dispatch = useDispatch()
    const { premium } = domain

    function checkAutoRenew() {
        if (!addedItem) {
            return true
        }

        return addedItem.auto_renew
    }

    if (premium) {
        return null
    }

    return (
        <RowWrapper>
            <NXUIForm.Switch
                checked={checkAutoRenew()}
                disabled={!addedItem}
                onClick={() => dispatch(toggleAutoRenew({
                    product_id: id,
                    name: searchTerm,
                    auto_renew: checkAutoRenew
                }))}
            />
        </RowWrapper>
    )
}

const IdProtection = ({ id, allowIdProtection, searchTerm, addedItem }: IdProtectionProps) => {
    const dispatch = useDispatch()

    function checkIDProtection() {
        if (!addedItem) {
            return true
        }

        return addedItem.id_protection
    }

    if (!allowIdProtection) {
        return null
    }

    return (
        <RowWrapper>
            <NXUIForm.Switch
                checked={checkIDProtection()}
                disabled={!!addedItem}
                onClick={() => {
                    return dispatch(toggleIDProtection({
                        product_id: id,
                        name: searchTerm,
                        id_protection: checkIDProtection()
                    }))
                }}
            />
        </RowWrapper>
    )
}

const SelectItem = ({ domain, items, searchTerm, billingCycle, setLightboxStatus, cart }: SelectItemProps) => {
    const dispatch = useDispatch()
    const { availability, premium, pricing, product: { id }, meta: { allow_id_protection } } = domain
    const [updateCartItems] = useUpdateCartItemsMutation()

    function addToCart() {
        const itemPricing = pricing.find(({ type, billing_cycle_id }) => {
            return type === 'transfer' && billing_cycle_id === billingCycle
        })

        setLightboxStatus(true)

        if (cart?.uuid) {
            const newItem = {
                product_id: id,
                billing_cycle_id: billingCycle,
                name: searchTerm,
                order_type: 'transfer'
            }

            updateCartItems({ uuid: cart.uuid, items: [newItem] })
        }

        dispatch(addToItems({
            name: searchTerm,
            product_type: 'domain',
            order_type: 'transfer',
            product_id: id,
            billing_cycle_id: billingCycle,
            pricing: itemPricing?.price,
            pricing_list: pricing,
            ...(!premium && { auto_renew: true }),
            ...(allow_id_protection && { id_protection: true })
        }))
    }

    function removeFromCart() {
        dispatch(removeFromItemsByNameID({
            product_id: id,
            name: searchTerm
        }))
    }

    if (availability) {
        return <span>Not Available</span>
    }

    if (items.some(({ product_id }) => product_id === id)) {
        return <Anchor color={'primary'} onClick={removeFromCart}>Remove</Anchor>
    }

    return <Anchor color={'primary'} onClick={addToCart}>Select</Anchor>
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const RowWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`

const DomainName = styled.span`
    word-break: break-word;
`

const StyledTransfer = styled.main`
    width: 100%;
    
    table {
        thead {
            tr {
                th:not(:first-child) {
                    span {
                        justify-content: center;
                    }
                }

                th:nth-child(5) {
                    max-width: 100px;
                    text-align: center;
                }
            }
        }
    }
`
